import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import { Button, Collapse, MenuItem, Paper } from '@material-ui/core'
import { useEffect, useState } from 'react'

import { getTranslate } from '../../localization'
import useStyles from './styles/PromotionsFilter.styles'

export interface PromotionFilters {
  providerCode: string
  status: string
}

export const EMPTY_PROMOTION_FILTERS: PromotionFilters = {
  providerCode: '',
  status: ''
}

// '' is the "any status" sentinel and is rendered as "All statuses". It is deliberately not a
// backend enum value — the query omits `status` entirely when this is selected.
const STATUSES = ['', 'Draft', 'Active', 'Paused', 'Expired', 'Archived']

interface PromotionsFilterProps {
  open: boolean
  /** The filters currently in effect — i.e. those the list was last queried with. */
  applied: PromotionFilters
  onApply: (filters: PromotionFilters) => void
}

const PromotionsFilter: React.FC<PromotionsFilterProps> = ({
  open,
  applied,
  onApply
}) => {
  const classes = useStyles()

  // Draft state, separate from `applied`. Editing a field does not re-query — the user sets
  // several fields and pays for one round trip on Apply. See portal-ux-conventions.md section 2.
  const [draft, setDraft] = useState<PromotionFilters>(applied)

  // Re-sync when the applied filters change from outside this component (back/forward
  // navigation restoring filters from the URL). Without this the panel would show stale draft
  // values that no longer describe the list beneath it.
  useEffect(() => {
    setDraft(applied)
  }, [applied])

  return (
    <Collapse in={open}>
      {/*
        The id lives on the Paper, not the Collapse — MUI v4's CollapseProps has no `id`, and
        the header's aria-controls needs to point at something that actually carries it.
      */}
      <Paper
        id="promotions-filter-panel"
        className={classes.panel}
        elevation={0}>
        <div className={classes.fields}>
          <InputWithLabel
            label={getTranslate('Provider code')}
            placeholder={getTranslate('Filter by provider code')}
            autoComplete="off"
            noHelperText
            fullWidth
            inputSize="small"
            data-cy="promotionFilterProviderCode"
            value={draft.providerCode}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                providerCode: event.currentTarget.value
              }))
            }
          />
          <SelectWithLabel
            label={getTranslate('Status')}
            noHelperText
            fullWidth
            size="small"
            data-cy="promotionFilterStatus"
            value={draft.status}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                status: event.target.value as string
              }))
            }>
            {STATUSES.map((value) => (
              <MenuItem key={value || 'all'} value={value}>
                {value ? getTranslate(value) : getTranslate('All statuses')}
              </MenuItem>
            ))}
          </SelectWithLabel>
        </div>
        <div className={classes.actions}>
          {/*
            Reset clears the fields; it does NOT re-query. Apply is the only thing that touches
            the list, matching BookingListFilter.tsx:993-1029 — where Reset calls setters only.
            Clearing and querying in one click would make "Reset" mean two different things
            depending on whether the user then pressed Apply.
          */}
          {/*
            Copied, not assigned. EMPTY_PROMOTION_FILTERS is a module-level singleton also used
            by readFiltersFromUrl in index.tsx; putting the shared object itself into component
            state is safe only while every update goes through a spread. A defensive copy means
            a future in-place edit here cannot reach the other consumer.
          */}
          <Button
            data-cy="promotionFilterReset"
            onClick={() => setDraft({ ...EMPTY_PROMOTION_FILTERS })}>
            {getTranslate('Reset')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            data-cy="promotionFilterApply"
            onClick={() => onApply(draft)}>
            {getTranslate('Apply')}
          </Button>
        </div>
      </Paper>
    </Collapse>
  )
}

export default PromotionsFilter
